import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { Mail, MapPin, Phone, Store, X } from "lucide-react";
import { RevendaInfoDetailsDTO } from "../dto/CompanyDTO";

function InfoLine({
  icon,
  label,
  value,
}: {
  icon?: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} py={0.75}>
      {icon && (
        <Box sx={{ color: "#94a3b8", display: "flex", alignItems: "center", flexShrink: 0 }}>
          {icon}
        </Box>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          fontSize="0.7rem"
          fontWeight={500}
          color="#94a3b8"
          textTransform="uppercase"
          letterSpacing={0.5}
        >
          {label}
        </Typography>
        <Typography fontSize="0.875rem" fontWeight={500} color="#1e293b">
          {value}
        </Typography>
      </Box>
    </Stack>
  );
}

interface RevendaInfoDialogProps {
  open: boolean;
  onClose: () => void;
  data: RevendaInfoDetailsDTO | null;
}

export function RevendaInfoDialog({ open, onClose, data }: RevendaInfoDialogProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 25px 50px -12px rgba(0,0,0,.15)",
        },
      }}
    >
      {data && (
        <>
          <Box
            sx={{
              background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
              px: 3,
              pt: 3,
              pb: 3,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Store size={24} color="#fff" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={700} fontSize="1.1rem" color="#fff" noWrap>
                {data.nome ?? "Lojista"}
              </Typography>
              <Typography fontSize="0.75rem" color="rgba(255,255,255,.75)" fontWeight={500}>
                {data.documento ?? "—"}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              sx={{ ml: "auto", color: "rgba(255,255,255,.7)", "&:hover": { color: "#fff" } }}
              size="small"
            >
              <X size={18} />
            </IconButton>
          </Box>

          <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
            <Stack spacing={0} divider={<Divider sx={{ borderColor: "#f1f5f9" }} />}>
              <InfoLine icon={<Mail size={16} />} label="E-mail" value={data.email ?? "—"} />
              <InfoLine
                icon={<Phone size={16} />}
                label="Telefone"
                value={data.telefone ?? "—"}
              />
            </Stack>

            <Box mt={2} mb={1}>
              <Typography
                fontSize="0.7rem"
                fontWeight={600}
                color="#cbd5e1"
                textTransform="uppercase"
                letterSpacing={1}
                mb={0.5}
              >
                Endereço
              </Typography>
              <Box sx={{ bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0", p: 2 }}>
                <Stack spacing={0} divider={<Divider sx={{ borderColor: "#e2e8f0" }} />}>
                  <InfoLine
                    icon={<MapPin size={16} />}
                    label="Logradouro"
                    value={data.logradouro ?? "—"}
                  />
                  <InfoLine label="Bairro" value={data.bairro ?? "—"} />
                  <InfoLine label="Cidade/UF" value={data.cidade ?? "—"} />
                </Stack>
                <InfoLine label="CEP" value={data.cep ?? "—"} />
              </Box>
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button
              onClick={onClose}
              variant="contained"
              disableElevation
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                bgcolor: "#e2e8f0",
                color: "#475569",
                "&:hover": { bgcolor: "#cbd5e1" },
              }}
            >
              Fechar
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
